import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import api from "../../api";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../../utils/toastMessages";
import crossIcon from "../../assets/icons/crossIcon.png";
import fileIcon from "../../assets/icons/sboxfile.png";
import linkIcon from "../../assets/icons/sboxlink.png";
import { BsArrowRepeat } from "react-icons/bs";
import { toast } from "react-toastify";
import { formatDateToUserTimeZone } from "../../utils/formatDate";
import axios from "axios";


const DEFAULT_LIMIT = 123360; // change this if you want a different limit

const TrainingYouTube = () => {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedYoutubeLink, setUploadedYoutubeLink] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingIndex, setLoadingIndex] = useState(null);

  // 🆕 summary states
  const [summaryDataList, setSummaryDataList] = useState([]);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  const [youtubeSummary, setYoutubeSummary] = useState({
    totalCharacters: 0,
    limit: DEFAULT_LIMIT,
    completion: 0,
    breakdown: [],
  });

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  // ---------- Helpers to parse characters robustly ----------
  const getCharsFromItem = (item) => {
    // Try multiple possible keys your API might return
    return (
      item?.characters ??
      item?.character_count ??
      item?.char_count ??
      item?.chars ??
      item?.length ??
      0
    );
  };

  // ✅ New function: handle status change (approved / rejected / pending)
  const handleChangeStatus = async (fileId, newStatus) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/approve-or-reject`,
        { file_id: fileId, approval_status: newStatus, notes: "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccessToast(`Status updated to ${newStatus}`);
      fetchYoutubeLinks();
    } catch (err) {
      console.error("❌ Error updating status:", err);
      showErrorToast("Failed to update status");
    }
  };

  const parseChars = (val) => {
    if (val == null) return 0;
    if (typeof val === "number") return Number.isFinite(val) ? val : 0;

    // try to parse numeric-like strings (handles "1,234" etc.)
    const str = String(val).replace(/,/g, "").trim();
    const n = Number(str);
    if (Number.isFinite(n)) return n;

    // fallback: extract first numeric sequence
    const m = String(val).match(/-?\d+(\.\d+)?/);
    return m ? Number(m[0]) : 0;
  };

  // ---------- Compute summary ----------
  const computeYoutubeSummary = (youtubeLinks = []) => {
    const limit = DEFAULT_LIMIT;

    if (!Array.isArray(youtubeLinks) || youtubeLinks.length === 0) {
      return { totalCharacters: 0, limit, completion: 0, breakdown: [] };
    }

    // total characters (sanitized)
    const totalCharacters = youtubeLinks.reduce((sum, item) => {
      const raw = getCharsFromItem(item);
      return sum + parseChars(raw);
    }, 0);

    // completion % (one decimal) and clamp to 100
    let completion = 0;
    if (limit > 0) {
      completion = (totalCharacters / limit) * 100;
      completion = Number(completion.toFixed(1));
      if (!Number.isFinite(completion) || completion < 0) completion = 0;
      if (completion > 100) completion = 100; // clamp - change if you want >100 display
    }

    // breakdown grouping:
    // - prefer identifying "YouTube" links (youtube.com / youtu.be)
    // - fallback to format detection (mp4, mov, avi, mkv, webm)
    // - otherwise "Other"
    const typeMap = {};
    youtubeLinks.forEach((item) => {
      const urlLower = String(item?.url || "").toLowerCase();

      let type = "Other";
      if (urlLower.includes("youtube.com") || urlLower.includes("youtu.be")) {
        type = "YouTube";
      } else if (urlLower.includes("mp4")) {
        type = "MP4";
      } else if (urlLower.includes("mov")) {
        type = "MOV";
      } else if (urlLower.includes("avi")) {
        type = "AVI";
      } else if (urlLower.includes("mkv")) {
        type = "MKV";
      } else if (urlLower.includes("webm")) {
        type = "WebM";
      }

      const chars = parseChars(getCharsFromItem(item));
      typeMap[type] = (typeMap[type] || 0) + chars;
    });

    const breakdown = Object.entries(typeMap).map(([type, chars]) => ({
      type,
      chars,
      value:
        totalCharacters > 0 ? Math.round((chars / totalCharacters) * 100) : 0,
    }));

    // sort by contribution desc
    breakdown.sort((a, b) => b.value - a.value);

    return { totalCharacters, limit, completion, breakdown };
  };

  // ---------- Fetch youtube links ----------
  useEffect(() => {
    fetchYoutubeLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchYoutubeLinks = async () => {
    try {
      setIsLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/youtube-urls`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // be resilient to response shape
      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.uploaded_youtube_urls || res.data?.data || [];

      setUploadedYoutubeLink(data);

      // compute summary immediately after fetch
      const computed = computeYoutubeSummary(data);
      setYoutubeSummary(computed);
    } catch (err) {
      console.error("❌ Error fetching uploaded Video  URLs:", err);
      showErrorToast("Error loading Video URL(s) links");
    } finally {
      setIsLoading(false);
    }
  };

  // recompute summary if uploadedYoutubeLink changes (covers manual edits elsewhere)
  useEffect(() => {
    const computed = computeYoutubeSummary(uploadedYoutubeLink);
    setYoutubeSummary((prev) => {
      // avoid unnecessary state updates
      if (
        prev.totalCharacters === computed.totalCharacters &&
        prev.completion === computed.completion &&
        prev.breakdown.length === computed.breakdown.length
      ) {
        return prev;
      }
      return computed;
    });
  }, [uploadedYoutubeLink]);

  // ---------- Upload, regenerate, modal logic ----------

  // 🆕 Fetch summary after upload
  const fetchSummaryFromBackend = async (uploadIds) => {
    try {
      const idsQuery = uploadIds.join(",");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/summary?ids=${idsQuery}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSummaryDataList(res.data);
      setShowSummaryModal(true);
    } catch (err) {
      console.error("Failed to fetch summaries:", err);
      showErrorToast("Failed to fetch summaries.");
    }
  };

  // 🆕 Regenerate specific summary
  const regenerateSummary = async (fileId, index) => {
    try {
      setLoadingIndex(index); // 🚫 disable button for this index

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/regenerate-summary`,
        { file_id: fileId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const newSummary = res.data.summary;

      setSummaryDataList((prevList) => {
        const updatedList = [...prevList];
        updatedList[index] = {
          ...updatedList[index],
          summary: newSummary,
        };
        return updatedList;
      });

      showSuccessToast("Summary regenerated!");
    } catch (err) {
      console.error("Failed to regenerate summary:", err);
      showErrorToast("Regeneration failed.");
    } finally {
      setLoadingIndex(null); // ✅ re-enable button
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!youtubeUrl.trim()) {
      showErrorToast("Please enter a valid Video URL.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/uploaded-files/upload_youtube`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
          body: JSON.stringify({ youtube_urls: [youtubeUrl] }),
        }
      );

      const result = await response.json();
      console.log("YouTube Upload Result:", result);

      // 🔥 backend duplicate response (HTTP 409)
      if (response.status === 409) {
        const msg =
          result?.message || "This video URL(s) already exists. No new upload.";
        showErrorToast(msg);
        setIsSubmitting(false);
        return;
      }

      const uploaded_count = result?.uploaded_files?.length || 0;
      const duplicate_count = result?.duplicate_skipped?.length || 0;
      const total = uploaded_count + duplicate_count;

      // 🔥 three toast conditions
      if (duplicate_count === total && uploaded_count === 0) {
        showErrorToast("This video URL(s) already exists. No new upload.");
      } else if (uploaded_count > 0 && duplicate_count > 0) {
        toast.info(
          `Uploaded ${uploaded_count} new video(s), ${duplicate_count} skipped as duplicate.`
        );
      } else if (uploaded_count > 0 && duplicate_count === 0) {
        showSuccessToast("Video URL(s) uploaded successfully.");
      }

      // reset input
      setYoutubeUrl("");
      fetchYoutubeLinks();

      // summary fetch for newly processed videos
      // const uploadedItems = result.uploaded_files || [];
      // const uploadIds = uploadedItems.map((item) => item.python_file_uuid);

      // if (uploadIds.length > 0) {
      //   await fetchSummaryFromBackend(uploadIds);
      // }
    } catch (error) {
      console.error("Failed to process video URL(s):", error);
      showErrorToast("Failed to process video URL(s). Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    alert(`Delete not implemented. File ID: ${id}`);
    // Implement DELETE API if needed
  };

  // ✅ Utility to detect and render clickable links (without "YouTube:" prefix)
  const renderWithLinks = (text) => {
    if (!text) return null;

    // Remove "URL:" prefix if exists
    const cleanText = text.replace(/^YouTube:\s*/i, "");

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return cleanText.split(urlRegex).map((part, i) =>
      urlRegex.test(part) ? (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none"
          style={{ color: "#0d6efd", fontWeight: 500 }}
        >
          {part}
        </a>
      ) : (
        part
      )
    );
  };
  const convertToValidDate = (dateStr) => {
    // Input: 27/11/2025, 2:30:44 pm
    const [date, time] = dateStr.split(", ");
    const [day, month, year] = date.split("/");

    // Convert time to 24-hour
    let [t, modifier] = time.split(" ");
    let [h, m, s] = t.split(":");

    h = Number(h);
    if (modifier.toLowerCase() === "pm" && h !== 12) h += 12;
    if (modifier.toLowerCase() === "am" && h === 12) h = 0;

    return `${year}-${month}-${day} ${String(h).padStart(2, "0")}:${m}:${s}`;
  };

  return (
    <main
      className="container-fluid mt-2 h-100"
      style={{ paddingBottom: "800px" }}
    >
      <div className="row">
        {/* LEFT PANEL */}
        <div className="col-lg-8 mb-4">
          <div
            className="card profile border mb-4 p-4"
            // style={{ borderColor: "#e6e7ea" }}
          >
            <h6 className="fw-semibold" style={{ fontSize: "16px" }}>
              Video URLs
            </h6>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Add URL</label>
                <div
                  className="p-2 rounded"
                  // style={{
                  //   backgroundColor: "#FAF9F9",
                  //   border: "1px solid #3e3e3e",
                  // }}
                >
                  <input
                    type="url"
                    className="form-control "
                    // style={{ backgroundColor: "transparent" }}
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://example.com/your-link"
                    required
                    disabled={isSubmitting} // ⛔ input disabled while submitting
                  />
                </div>
              </div>
              <button
                type="submit"
                className="custom-btn  w-100 fw-semibold"
                // style={{ backgroundColor: "#3e3e3e" }}
                disabled={isSubmitting} // ⛔ button disabled while submitting
              >
                {isSubmitting ? "Uploading..." : "Upload"}
              </button>
            </form>
          </div>

          {/* Table */}
          <div className="mt-4">
            <div
              className="table-responsive"
              style={{ maxHeight: "330px", overflowY: "auto" }}
            >
              <table className="table text-left trainingUrls-table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Video URL's</th>
                    <th>Total Characters</th>
                    <th>Uploaded On</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="4">Loading...</td>
                    </tr>
                  ) : uploadedYoutubeLink.length > 0 ? (
                    uploadedYoutubeLink.map((item, index) => (
                      <tr key={item.id || index}>
                        <td
                          className="text-truncate"
                          style={{ maxWidth: "300px" }}
                          title={item.url}
                        >
                          {/* ✅ clickable link render */}
                          {renderWithLinks(item.url)}
                        </td>
                        <td>{item.total_tokens || 0}</td>
                        <td>{formatDateToUserTimeZone(item.updatedDate)}</td>
                        <td className="text-left">
                      <select
                        className={`badge ${
                          item.status === "approved"
                            ? "bg-success"
                            : item.status === "pending"
                            ? "bg-warning"
                            : "bg-danger "
                        }`}
                        value={item.status}
                        onChange={(e) =>
                          handleChangeStatus(item.id, e.target.value)
                        }
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </td>
                        {/* <td>
                          <button className="btn btn-sm btn-outline-dark">
                            <i className="bi bi-eye" />
                          </button>
                        </td> */}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-muted">
                        There are no video URL(s) in this section.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: Summary */}
        <div className="col-lg-4">
          <div className="card profile p-4 shadow-sm">
            <h5 className="fw-semibold  mb-4">Summary</h5>
            <div className="d-flex justify-content-center align-items-center mb-4 position-relative">
              <svg
                width="140"
                height="140"
                viewBox="0 0 36 36"
                className="circular-chart"
              >
                <path
                  className="circle-bg"
                  d="M18 2.0845
                    a 15.9155 15.9155 0 0 1 0 31.831
                    a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#eee"
                  strokeWidth="2"
                />
                <path
                  className="circle"
                  strokeDasharray={`${youtubeSummary.completion}, 100`}
                  d="M18 2.0845
    a 15.9155 15.9155 0 0 1 0 31.831
    a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none"
                  stroke="#3e3e3e"
                  strokeWidth="2"
                />
              </svg>
              <div className="position-absolute text-center">
                <div className=" small">Complete</div>
                <div className="fw-bold fs-2">{youtubeSummary.completion}%</div>
              </div>
            </div>
            <div className="row text-center mb-4">
              <div className="col">
                <div className=" small fw-semibold">Total Characters</div>
                <div className="fw-semibold">
                  {youtubeSummary.totalCharacters.toLocaleString()}
                </div>
              </div>
              <div className="col">
                <div className=" small fw-semibold">Limit</div>
                <div className="fw-semibold">
                  {youtubeSummary.limit.toLocaleString()}
                </div>
              </div>
            </div>

            {[
              { type: "MP4", value: 80 },
              { type: "MOV", value: 60 },
              { type: "AVI", value: 40 },
              { type: "MKV", value: 50 },
              { type: "WebM", value: 70 },
            ].map((item, idx) => (
              <div key={idx} className="mb-3">
                <div className="d-flex justify-content-between small fw-semibold">
                  <span className="text-secondary">{item.type}</span>
                </div>
                <div className="progress" style={{ height: "6px" }}>
                  <div
                    className="progress-bar bg-dark"
                    role="progressbar"
                    style={{ width: `${item.value}%` }}
                  />
                </div>
              </div>
            ))}

            {/* Dynamic breakdown */}
            {/* {youtubeSummary.breakdown && youtubeSummary.breakdown.length > 0 ? (
              youtubeSummary.breakdown.map((item, idx) => (
                <div key={idx} className="mb-3">
                  <div className="d-flex justify-content-between small fw-semibold">
                    <span className="text-secondary">{item.type}</span>
                    <span>{item.value}%</span>
                  </div>
                  <div className="progress" style={{ height: "6px" }}>
                    <div
                      className="progress-bar bg-dark"
                      role="progressbar"
                      style={{ width: `${item.value}%` }}
                      aria-valuenow={item.value}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-muted small">No data available.</div>
            )} */}
          </div>
        </div>
      </div>

      {/* 🆕 Summary Modal */}
      {/* {showSummaryModal && (
        <div className="modal-backdrop-custom d-flex justify-content-center align-items-center">
          <div
            className="p-4 rounded-3 text-start position-relative"
            style={{
              backgroundColor: "#ffffff",
              width: "90%",
              maxWidth: "800px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <img
              src={crossIcon}
              alt="Close"
              className="custom-close-icon"
              onClick={() => setShowSummaryModal(false)}
            />

            <p className="fw-bold mt-2 ps-3 mb-3">
              Summaries from uploaded Documents
            </p>

            {summaryDataList.length === 0 ? (
              <p>No summaries available.</p>
            ) : (
              summaryDataList.map((summary, index) => (
                <div key={index} className="rounded-3 p-3 mb-3 bg-light">
                  <div className="mb-2 d-flex align-items-center gap-3">
                    {summary.type === "file" ? (
                      <img src={fileIcon} alt="file" width={20} height={20} />
                    ) : (
                      <img src={linkIcon} alt="link" width={20} height={20} />
                    )}
                    <div>
                      <div className=" text-truncate">
                        {summary.name || "Untitled"}
                      </div>
                    </div>
                  </div>

                  <div className="position-relative">
                    <textarea
                      className="form-control"
                      rows={5}
                      value={summary.summary || "No summary available."}
                      readOnly
                    />
                    <button
                      className="btn btn-link position-absolute text-white end-0 mt-1 bg-dark me-2"
                      onClick={() =>
                        regenerateSummary(summary?.approval_request_id, index)
                      }
                      disabled={loadingIndex === index} // 🚫 prevent double click
                    >
                      {loadingIndex === index ? (
                        <span className="spinner-border spinner-border-sm text-light" />
                      ) : (
                        <BsArrowRepeat size={18} />
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}

            <div className="text-center mt-3">
              <button
                className="btn bg-dark text-white w-25"
                onClick={() => setShowSummaryModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )} */}
    </main>
  );
};

export default TrainingYouTube;
